import React from 'react';
import { useLocation } from 'react-router-dom';
import BackButton from '../ui/back-button';

/**
 * GlobalBackButton
 *
 * Renders a floating WhatsApp/Instagram-style back button on PUBLIC /
 * standalone routes (auth, landing, help, legal, pricing, onboarding etc.)
 * that don't use DashboardLayout / AdminLayout.
 *
 * Shows on both PWA and PC web so users on the desktop browser have the
 * same navigation affordance they did prior to the PWA-only experiment.
 *
 * Home-style pages (Landing, Dashboard, Admin root) hide the button.
 */

// Root/home routes never show the back button
const HOME_ROUTES = new Set(['/', '/dashboard', '/admin', '/setup-complete']);

// Only these public/standalone paths receive the FIXED global back button.
// Everything else lives inside DashboardLayout / AdminLayout which renders
// its own inline back button in the content area.
const PUBLIC_EXACT = new Set([
  '/pricing',
  '/help',
  '/privacy-policy',
  '/terms-of-service',
  '/cookie-policy',
  '/camera-test',
  '/accept-invitation',
  '/verify-email',
  '/verify-email-pending',
  '/reset-password',
  '/app-welcome',
  '/oauth/plaid',
]);
const PUBLIC_PREFIXES = ['/help/'];

const isPublicRoute = (pathname) => {
  if (PUBLIC_EXACT.has(pathname)) return true;
  if (PUBLIC_PREFIXES.some((p) => pathname.startsWith(p))) return true;
  return false;
};

const pickFallback = (pathname) => {
  if (pathname.startsWith('/admin')) return '/admin';
  return '/';
};

const GlobalBackButton = () => {
  const { pathname } = useLocation();
  if (HOME_ROUTES.has(pathname)) return null;
  if (!isPublicRoute(pathname)) return null;

  return (
    <div
      className="fixed z-[70] top-[max(0.75rem,env(safe-area-inset-top))] left-[max(0.75rem,env(safe-area-inset-left))] print:hidden"
      data-testid="global-back-button-wrapper"
    >
      <BackButton fallbackPath={pickFallback(pathname)} />
    </div>
  );
};

export default GlobalBackButton;
